"use client"

import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { withAmazonTagForPlacement } from "@/lib/affiliate"
import { contentAnalytics, type EventProperties } from "@/lib/analytics"

const ASIN_IN_URL = /\/(?:dp|gp\/product)\/([A-Z0-9]{10})(?:[/?#]|$)/i

type Scalar = string | number | boolean | null | undefined

/** One Amazon CTA on a /tools route. */
export interface ToolAffiliateTarget {
  /** Unified affiliate surface, e.g. `tool-grub-control-calculator`. Also the `data-affiliate-placement` value. */
  surface: string
  /** Tool route slug; stamped as `article_slug: tool:<slug>`. */
  toolSlug: string
  productName: string
  /** Amazon URL as built by `amazonUrl` / `amazonSearchUrl` (site tag). Re-tagged for the surface's family here. */
  url: string
  /** 1-based slot within the rendered set. */
  position: number
  /** Defaults to the ASIN in the URL; search URLs have none. */
  asin?: string | null
  /** Extra properties stamped on BOTH the impression and the click. */
  eventProps?: Record<string, Scalar>
}

export interface ToolAffiliateEvents {
  href: string
  asin: string | undefined
  impression: EventProperties
  click: {
    productName: string
    affiliateLink: string
    asin: string | undefined
    [key: string]: Scalar
  }
}

/**
 * The impression and click payloads for one tool CTA. Both carry the same
 * surface, ASIN, 1-based `position` and 0-based `pick_index` (the key older
 * tool surfaces used), so `affiliate_product_shown` joins
 * `affiliate_product_clicked` per ASIN and per slot.
 */
export function toolAffiliateEvents(target: ToolAffiliateTarget): ToolAffiliateEvents {
  const href = withAmazonTagForPlacement(target.url, target.surface)
  const asin = (target.asin ?? href.match(ASIN_IN_URL)?.[1]?.toUpperCase()) || undefined
  const shared: Record<string, Scalar> = {
    ...target.eventProps,
    article_slug: `tool:${target.toolSlug}`,
    position: target.position,
    pick_index: target.position - 1,
  }
  return {
    href,
    asin,
    impression: {
      ...shared,
      product_name: target.productName,
      affiliate_link: href,
      asin,
    } as EventProperties,
    click: {
      ...shared,
      productName: target.productName,
      affiliateLink: href,
      asin,
    },
  }
}

export interface ToolAffiliateAnchorProps {
  href: string
  target: "_blank"
  rel: string
  "data-affiliate": "true"
  "data-affiliate-placement": string
  "data-affiliate-position": number
  onClick: () => void
}

/**
 * Impression ref (attach to the card root) plus spread-ready anchor props for a
 * tool's Amazon CTA. The impression fires once per mount, so key the card on
 * the product when the product can change under it. `null` renders nothing
 * tracked: the ref no-ops and `anchorProps` is null.
 */
export function useToolAffiliateCta(target: ToolAffiliateTarget): {
  ref: (node?: Element | null) => void
  anchorProps: ToolAffiliateAnchorProps
}
export function useToolAffiliateCta(target: ToolAffiliateTarget | null): {
  ref: (node?: Element | null) => void
  anchorProps: ToolAffiliateAnchorProps | null
}
export function useToolAffiliateCta(target: ToolAffiliateTarget | null) {
  const events = target ? toolAffiliateEvents(target) : null
  const ref = useAffiliateImpression(target && events ? target.surface : undefined, events?.impression)

  const anchorProps: ToolAffiliateAnchorProps | null =
    target && events
      ? {
          href: events.href,
          target: "_blank",
          rel: "noopener noreferrer nofollow sponsored",
          "data-affiliate": "true",
          "data-affiliate-placement": target.surface,
          "data-affiliate-position": target.position,
          onClick: () => contentAnalytics.affiliateProductClicked(target.surface, events.click),
        }
      : null

  return { ref, anchorProps }
}
